import { I as Icon, i as iconDefinition } from './definition28.js';
import { B as Button, b as buttonDefinition } from './definition11.js';
import { E as Elevation, e as elevationDefinition } from './definition65.js';
import { V as VividElement, a as attr, o as observable, h as html, d as createRegisterFunction, f as defineVividComponent } from './vivid-element.js';
import { h as handleEscapeKeyAndStopPropogation } from './index.js';
import { D as DelegatesAria, d as delegateAria } from './delegates-aria.js';
import { L as Localized } from './localized.js';
import { c as classNames } from './class-names.js';
import { w as when } from './when.js';
import { s as slotted } from './slotted.js';

const styles = ".base{white-space:initial;box-sizing:border-box;padding:0;border:none;border-radius:8px;background-color:transparent;color:var(--vvd-color-canvas-text);max-inline-size:var(--dialog-max-inline-size, var(--_dialog-default-max-inline-size));min-inline-size:var(--dialog-min-inline-size, 280px)}@media not all and (width >= 600px){.base{--_dialog-default-max-inline-size: 90vw}}@media (width >= 600px){.base{--_dialog-default-max-inline-size: 560px}}.base:not(.scrollable-body){max-block-size:var(--dialog-max-block-size, calc(100vh - 64px) )}.base:not(.modal){z-index:var(--dialog-z-index, 1);inset-block:var(--dialog-inset-block, auto);inset-inline:var(--dialog-inset-inline, 0)}.base.modal{box-shadow:0 4px 20px #00000059}.base.modal::backdrop{background-color:var(--vvd-color-canvas-text, currentColor);opacity:.5}.base:focus-visible{box-shadow:0 0 0 4px color-mix(in srgb,var(--vvd-color-cta-500),transparent 85%),inset 0 0 0 3px var(--focus-stroke-gap-color, currentColor);outline:1px solid var(--focus-stroke-color, var(--vvd-color-cta-500));outline-offset:calc(-1px - var(--focus-inset, 0px))}.main-wrapper{display:flex;box-sizing:border-box;flex-direction:column;gap:24px;padding-block:24px}.main-wrapper>*{box-sizing:border-box}.header{display:grid;align-items:flex-start;justify-content:space-between;inline-size:100%;padding-inline:24px}.base:not(.icon-placement-side) .header{grid-template-columns:1fr auto;grid-template-rows:auto auto auto}.base:not(.icon-placement-side) .header .icon{font-size:calc(1px*(32 + 4*clamp(-1,var(--vvd-size-density, 0),2)));grid-column:1/2;grid-row:1/2;line-height:1;margin-block-end:calc(1px*(24 + 4*clamp(-1,var(--vvd-size-density, 0),2)))}.base.icon-placement-side .header{grid-template-columns:auto 1fr auto;grid-template-rows:auto auto}.base.icon-placement-side .header .icon{font-size:calc(1px*(28 + 4*clamp(-1,var(--vvd-size-density, 0),2)))}.base.icon-placement-side .header ::slotted(*),.base.icon-placement-side .header .icon{grid-column:1/2;grid-row:1/-1;margin-inline-end:calc(1px*(32 + 4*clamp(-1,var(--vvd-size-density, 0),2))/2)}.header.border{position:relative}.header.border:after{position:absolute;bottom:-24px;left:0;background-color:var(--vvd-color-neutral-200);block-size:1px;content:\"\";inline-size:100%}.base.hide-body .header.border:after{content:none}.body:not(.full-width){padding-inline:24px}.hide-body .body{display:none}.scrollable-body .body{overflow:hidden auto;max-block-size:var(--dialog-body-max-block-size, 300px);--scrollbar-track-color: transparent;--scrollbar-thumb-color: color-mix(in srgb, var(--vvd-color-neutral-950), transparent 70%)}.scrollable-body .body{scrollbar-color:var(--scrollbar-thumb-color) var(--scrollbar-track-color);scrollbar-width:thin}.scrollable-body .body ::-webkit-scrollbar{width:4px}.scrollable-body .body ::-webkit-scrollbar-track{background:var(--scrollbar-track-color)}.scrollable-body .body ::-webkit-scrollbar-thumb{border:0;border-radius:4px;background-color:var(--scrollbar-fallback-track-color, var(--scrollbar-thumb-color))}.footer{display:flex;align-items:center;justify-content:space-between;gap:24px;padding-inline:24px}.hide-footer .footer{display:none}.footer .actions{display:flex;align-self:flex-end;gap:8px}.dismiss-button{flex-shrink:0;grid-column:-2/-1;grid-row:1/-1;margin-block-start:-8px;margin-inline:4px -8px}.headline{margin:0;font:var(--vvd-typography-heading-4)}.subtitle{margin:0;font:var(--vvd-typography-base)}slot[name=main]{--scrollbar-track-color: transparent;--scrollbar-thumb-color: color-mix(in srgb, var(--vvd-color-neutral-950), transparent 70%)}slot[name=main]{scrollbar-color:var(--scrollbar-thumb-color) var(--scrollbar-track-color);scrollbar-width:thin}slot[name=main] ::-webkit-scrollbar{width:4px}slot[name=main] ::-webkit-scrollbar-track{background:var(--scrollbar-track-color)}slot[name=main] ::-webkit-scrollbar-thumb{border:0;border-radius:4px;background-color:var(--scrollbar-fallback-track-color, var(--scrollbar-thumb-color))}slot[name=main]{display:block;overflow:hidden auto;max-width:inherit;max-height:inherit;border-radius:inherit}";

var __defProp = Object.defineProperty;
var __decorateClass = (decorators, target, key, kind) => {
  var result = void 0 ;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (decorator(target, key, result) ) || result;
  if (result) __defProp(target, key, result);
  return result;
};
class Dialog extends Localized(DelegatesAria(VividElement)) {
  constructor() {
    super(...arguments);
    this.open = false;
    this.fullWidthBody = false;
    this.dismissButtonAriaLabel = null;
    this.noLightDismiss = false;
    this.noDismissOnEsc = false;
    this.noDismissButton = false;
    this.nonDismissible = false;
    this.modal = false;
    this.scrollableBody = false;
    this._openedAsModal = false;
    this.#handleScrimClick = (event) => {
      if (event.target !== this.#dialog || !this.#isDismissibleVia("light-dismiss")) {
        return;
      }
      const rect = this.#dialog.getBoundingClientRect();
      const clickedInDialog = rect.top <= event.clientY && event.clientY <= rect.top + rect.height && rect.left <= event.clientX && event.clientX <= rect.left + rect.width;
      if (!clickedInDialog) {
        this._handleCloseRequest();
      }
    };
    this.#handleInternalFormSubmit = (event) => {
      if (event.target.method !== "dialog") {
        return;
      }
      this.open = false;
    };
  }
  /**
   * @internal
   */
  modalChanged(_, newValue) {
    if (this.open) {
      this._openedAsModal = newValue;
      if (this.$fastController.isConnected) {
        this.#closeDialog();
        this.#showDialog();
      }
    }
  }
  set returnValue(value) {
    if (this.#dialog) {
      this.#dialog.returnValue = value;
    }
  }
  get returnValue() {
    return this.#dialog?.returnValue;
  }
  #dialogElement;
  get #dialog() {
    if (!this.#dialogElement) {
      this.#dialogElement = this.shadowRoot.querySelector(
        "dialog"
      );
    }
    return this.#dialogElement;
  }
  /**
   * @internal
   */
  openChanged(oldValue, newValue) {
    if (oldValue === void 0) {
      return;
    }
    if (!newValue) {
      this._openedAsModal = false;
      if (this.$fastController.isConnected) {
        this.#closeDialog();
      }
      this.$emit("close", this.returnValue, { bubbles: false });
    } else {
      this._openedAsModal = this._openedAsModal || this.modal;
      if (this.$fastController.isConnected) {
        this.#showDialog();
      }
      this.$emit("open", void 0, { bubbles: false });
    }
  }
  get _showDismissButton() {
    return this.#isDismissibleVia("dismiss-button");
  }
  #isDismissibleVia(method) {
    if (this.nonDismissible) {
      return false;
    }
    switch (method) {
      case "escape":
        return !this.noDismissOnEsc;
      case "dismiss-button":
        return !this.noDismissButton;
      case "light-dismiss":
        return !this.noLightDismiss;
    }
  }
  #handleScrimClick;
  #handleInternalFormSubmit;
  /**
   * @internal
   */
  _onKeyDown(event) {
    if (handleEscapeKeyAndStopPropogation(event) && this._openedAsModal) {
      if (this.#isDismissibleVia("escape")) {
        this._handleCloseRequest();
      }
      return false;
    }
    return true;
  }
  /**
   * @internal
   */
  _handleCloseRequest() {
    if (this.$emit("cancel", void 0, {
      bubbles: false,
      cancelable: true
    })) {
      this.open = false;
    }
  }
  close() {
    this.open = false;
  }
  show() {
    if (this._openedAsModal && !this.modal) {
      throw new DOMException(
        "Failed to execute 'show' on 'Dialog': The dialog is already open as a modal dialog, and therefore cannot be opened as a non-modal dialog.",
        "InvalidStateError"
      );
    }
    this.open = true;
  }
  showModal() {
    if (this.open && !this._openedAsModal) {
      throw new DOMException(
        "Failed to execute 'showModal' on 'Dialog': The dialog is already open as a non-modal dialog, and therefore cannot be opened as a modal dialog.",
        "InvalidStateError"
      );
    }
    this._openedAsModal = true;
    this.open = true;
  }
  #closeDialog() {
    this.#dialog.close();
  }
  #showDialog() {
    if (this._openedAsModal) {
      this.#dialog.showModal();
    } else {
      this.#dialog.show();
    }
  }
  connectedCallback() {
    super.connectedCallback();
    if (this.open) {
      this.#showDialog();
    }
    this.#dialog.addEventListener("mousedown", this.#handleScrimClick);
    this.#dialog.addEventListener("submit", this.#handleInternalFormSubmit);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.open) {
      this.#closeDialog();
    }
    this.#dialog.removeEventListener("mousedown", this.#handleScrimClick);
    this.#dialog.removeEventListener("submit", this.#handleInternalFormSubmit);
  }
}
__decorateClass([
  attr({ mode: "boolean" })
], Dialog.prototype, "open");
__decorateClass([
  attr
], Dialog.prototype, "icon");
__decorateClass([
  attr({ attribute: "icon-placement" })
], Dialog.prototype, "iconPlacement");
__decorateClass([
  attr
], Dialog.prototype, "subtitle");
__decorateClass([
  attr
], Dialog.prototype, "headline");
__decorateClass([
  attr({ attribute: "full-width-body", mode: "boolean" })
], Dialog.prototype, "fullWidthBody");
__decorateClass([
  attr({ attribute: "dismiss-button-aria-label" })
], Dialog.prototype, "dismissButtonAriaLabel");
__decorateClass([
  attr({ attribute: "no-light-dismiss", mode: "boolean" })
], Dialog.prototype, "noLightDismiss");
__decorateClass([
  attr({ attribute: "no-dismiss-on-esc", mode: "boolean" })
], Dialog.prototype, "noDismissOnEsc");
__decorateClass([
  attr({ attribute: "no-dismiss-button", mode: "boolean" })
], Dialog.prototype, "noDismissButton");
__decorateClass([
  attr({ attribute: "non-dismissible", mode: "boolean" })
], Dialog.prototype, "nonDismissible");
__decorateClass([
  attr({ mode: "boolean" })
], Dialog.prototype, "modal");
__decorateClass([
  attr({ mode: "boolean", attribute: "scrollable-body" })
], Dialog.prototype, "scrollableBody");
__decorateClass([
  observable
], Dialog.prototype, "_openedAsModal");
__decorateClass([
  observable
], Dialog.prototype, "bodySlottedContent");
__decorateClass([
  observable
], Dialog.prototype, "footerSlottedContent");
__decorateClass([
  observable
], Dialog.prototype, "actionItemsSlottedContent");

const getClasses = ({
  iconPlacement,
  bodySlottedContent,
  footerSlottedContent,
  actionItemsSlottedContent,
  _openedAsModal,
  scrollableBody
}) => classNames(
  "base",
  [`icon-placement-${iconPlacement}`, Boolean(iconPlacement)],
  ["hide-body", !bodySlottedContent?.length],
  [
    "hide-footer",
    !(footerSlottedContent?.length || actionItemsSlottedContent?.length)
  ],
  ["modal", _openedAsModal],
  ["scrollable-body", Boolean(scrollableBody)]
);
function icon(iconTag) {
  return html`
		<${iconTag} class="icon" name="${(x) => x.icon}"></${iconTag}>
	`;
}
function headline() {
  return html` <h2 class="headline">${(x) => x.headline}</h2> `;
}
function subtitle() {
  return html` <h3 class="subtitle">${(x) => x.subtitle}</h3> `;
}
function renderHeaderText() {
  return html`
		<div class="header-text">${headline()} ${subtitle()}</div>
	`;
}
function renderDismissButton(buttonTag) {
  return html`
	<${buttonTag}
		aria-label="${(x) => x.dismissButtonAriaLabel || x.locale.dialog.dismissButtonLabel}"
		size="condensed"
		class="dismiss-button"
		icon="close-line"
		@click="${(x) => x._handleCloseRequest()}"
	></${buttonTag}>`;
}
const DialogTemplate = (context) => {
  const elevationTag = context.tagFor(Elevation);
  const iconTag = context.tagFor(Icon);
  const buttonTag = context.tagFor(Button);
  return html`
	<${elevationTag} dp="8" not-relative>
		<dialog class="${getClasses}"
				@keydown="${(x, c) => x._onKeyDown(c.event)}"
				@cancel="${(_, c) => c.event.preventDefault()}"
				${delegateAria({
    ariaModal: (x) => String(x._openedAsModal)
  })}
		>
			<slot name="main">
				<div class="main-wrapper">
					<div class="header ${(x) => x.subtitle ? "border" : ""}">
							<slot name="graphic">
								${when((x) => x.icon, icon(iconTag))}
							</slot>
							${when((x) => x.headline && x.subtitle, renderHeaderText())}
							${when((x) => x.headline && !x.subtitle, headline())}
							${when((x) => x.subtitle && !x.headline, subtitle())}
							${when((x) => x._showDismissButton, renderDismissButton(buttonTag))}
					</div>
					<div class="body ${(x) => x.fullWidthBody ? "full-width" : ""}" >
						<slot name="body" ${slotted("bodySlottedContent")}></slot>
					</div>
					<div class="footer">
						<div>
							<slot name="footer" ${slotted("footerSlottedContent")}></slot>
						</div>
						<div class="actions">
							<slot name="action-items" ${slotted("actionItemsSlottedContent")}></slot>
						</div>
					</div>
				</div>
			</slot>
		</dialog>
	</${elevationTag}>`;
};

const dialogDefinition = defineVividComponent(
  "dialog",
  Dialog,
  DialogTemplate,
  [iconDefinition, buttonDefinition, elevationDefinition],
  {
    styles
  }
);
const registerDialog = createRegisterFunction(dialogDefinition);

export { Dialog as D, dialogDefinition as d, registerDialog as r };
